import '../style/global.css';

import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { Box, FormControl, List, ListItemButton, ListItemText, MenuItem, Pagination, Select, Skeleton, Typography } from '@mui/material';
import React from "react";
import { useHistory, useLocation } from 'react-router-dom';
import { Sentence, SentenceApi } from '../api/SentenceApi';
import { handleErrorMessage } from './ErrorDialog';
import AppBarContext from '../context/AppBarContext';

export default function TopSentences() {
    const history = useHistory();
    const location = useLocation();
    const { setErrorMessage } = React.useContext(AppBarContext);

    const [isLoading, setIsLoading] = React.useState(true);
    const [showSkeleton, setShowSkeleton] = React.useState(false);
    const [recentScoreSentenceList, setRecentScoreSentenceList] = React.useState<Sentence[]>([]);
    const [mostScoreSentenceList, setMostScoreSentenceList] = React.useState<Sentence[]>([]);
    const [sortBy, setSortBy] = React.useState<'recent' | 'most'>('most');
    const [currentPage, setCurrentPage] = React.useState(1);

    const itemsPerPage = 10;

    React.useEffect(() => {
        const skeletonTimeout = setTimeout(() => setShowSkeleton(true), 300);

        Promise.all([
            SentenceApi.getSortedByRecentSentenceScore(),
            SentenceApi.getSortedByMostSentenceScore()
        ])
            .then(([recentRes, mostRes]) => {
                if (recentRes.status < 300) setRecentScoreSentenceList(recentRes.data);
                else throw new Error("Failed getting recent scores");

                if (mostRes.status < 300) setMostScoreSentenceList(mostRes.data);
                else throw new Error("Failed getting popular scores");
            })
            .catch(err => handleErrorMessage(err, setErrorMessage, history, location))
            .finally(() => setIsLoading(false));

        return () => clearTimeout(skeletonTimeout);
    }, [history, location, setErrorMessage]);

    const handleSortChange = (event: any) => {
        setSortBy(event.target.value);
        setCurrentPage(1);
    };

    const currentList = sortBy === 'recent' ? recentScoreSentenceList : mostScoreSentenceList;
    const paginatedList = currentList.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    const pageCount = Math.ceil(currentList.length / itemsPerPage);

    return (
        <div style={{ width: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            {/* Title & Sort Row */}
            <div style={{ width: '100%', position: 'relative', textAlign: 'center', marginBottom: 16 }}>
                <Typography variant="h2" sx={{ color: '#002A6E', flexGrow: 1 }}>
                    Top Sentences
                </Typography>

                <FormControl size="small" sx={{
                    position: { xs: 'static', sm: 'absolute' },
                    top: { sm: 0 },
                    right: { sm: 0 },
                    mt: { xs: 2, sm: 0 },
                }}>
                    <Select
                        id="leaderboard-sort-select"
                        value={sortBy}
                        onChange={handleSortChange}
                        variant="outlined"
                        displayEmpty
                        sx={{ fontSize: '0.875rem', backgroundColor: '#f9f9f9' }}
                    >
                        <MenuItem value="most">Popular</MenuItem>
                        <MenuItem value="recent">Recent</MenuItem>
                    </Select>
                </FormControl>
            </div>

            {/* Sentence List */}
            <Box sx={{ width: '100%', px: 4, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                {isLoading && showSkeleton ? (
                    <List sx={{ width: '100%', minHeight: 300 }}>
                        {Array.from({ length: itemsPerPage }).map((_, i) => (
                            <div key={i} style={{ margin: '8px 0' }}>
                                <Skeleton variant="rectangular" height={30} width="100%" sx={{ borderRadius: 1 }} />
                            </div>
                        ))}
                    </List>
                ) : (
                    <List sx={{ width: '100%' }}>
                        {paginatedList.map((sentence, index) => (
                            <ListItemButton
                                key={sentence.id}
                                onClick={() => history.push(`/leaderboard_sentence/${sentence.id}`)}
                            >
                                <ListItemText
                                    primary={`${(currentPage - 1) * itemsPerPage + index + 1}. ${sentence.text}`}
                                />
                                <ChevronRightRoundedIcon sx={{ color: 'gray' }} />
                            </ListItemButton>
                        ))}
                    </List>
                )}

                {/* Pagination */}
                {!isLoading && pageCount > 1 && (
                    <Pagination
                        count={pageCount}
                        page={currentPage}
                        onChange={(_, value) => setCurrentPage(value)}
                        color="primary"
                        siblingCount={2}
                        boundaryCount={2}
                        sx={{ mt: 2 }}
                    />
                )}
            </Box>

        </div>
    );
}
